<template>
  <v-card-text>
    <!-- Header con información rápida -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-alert
          type="info"
          prominent
          border="start"
          class="mb-4"
        >
          <v-icon slot="prepend">mdi-information</v-icon>
          <div class="text-h6">Vista de Solo Lectura</div>
          <!-- P13-V7B (refutation): the list is the open incidences of the supervised groups, not "tus" incidences. -->
          <div>Como supervisor, puedes ver el estado de las incidencias abiertas de los grupos que supervisas pero no editarlas directamente. Usa los comentarios para comunicarte con el equipo responsable.</div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Filtros de estado -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          v-model="statusFilter"
          :items="activeStatusOptions"
          label="Estado"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="applyFilters"
        >
          <template v-slot:prepend-inner>
            <v-icon>mdi-filter</v-icon>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" md="4">
        <v-select
          v-model="priorityFilter"
          :items="priorityOptions"
          label="Prioridad"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="applyFilters"
        >
          <template v-slot:prepend-inner>
            <v-icon>mdi-flag</v-icon>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" md="4">
        <v-btn
          @click="refreshData"
          :loading="loading"
          color="primary"
          variant="outlined"
          block
        >
          <v-icon left>mdi-refresh</v-icon>
          Actualizar Estado
        </v-btn>
      </v-col>
    </v-row>

    <!--
      P13-V7B (refutation) — Métricas rápidas. What is left counts the rows LISTED BELOW, and says so:
      "En Progreso" is `in_progress` only (it also counted `pending`), "Alta Prioridad" reads the
      server's own `high`/`critical`. "Atrasadas" is RETIRED with the per-card progress bar: both judged
      lateness against 1/3/7-day limits the client invented, keyed on 'alta'/'media' — a vocabulary the
      server never sends — so every incidence was measured against the 7-day default. The backend states
      no SLA or due date for this role; no lateness verdict is published without one.
    -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="metric-card text-center">
          <v-card-text>
            <v-avatar color="orange" size="48" class="mb-3">
              <v-icon color="white" size="24">mdi-clock</v-icon>
            </v-avatar>
            <div class="text-h5 font-weight-bold" data-testid="fs-active-in-progress">{{ displayMetric(metrics.inProgress) }}</div>
            <div class="text-caption text-grey">En Progreso (listadas)</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="metric-card text-center">
          <v-card-text>
            <v-avatar color="red" size="48" class="mb-3">
              <v-icon color="white" size="24">mdi-alert</v-icon>
            </v-avatar>
            <div class="text-h5 font-weight-bold" data-testid="fs-active-high-priority">{{ displayMetric(metrics.highPriority) }}</div>
            <div class="text-caption text-grey">Alta Prioridad (listadas)</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Lista de incidencias activas -->
    <v-row v-if="loading">
      <v-col cols="12">
        <div class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4 text-h6">Cargando incidencias activas...</div>
        </div>
      </v-col>
    </v-row>

    <!-- P13-V7B (refutation): a failed read is said as such; it used to fall through to the
         congratulation below. -->
    <v-row v-else-if="loadError">
      <v-col cols="12">
        <v-alert type="warning" variant="tonal" data-testid="fs-active-load-error">
          {{ loadError }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- P13-V7B (refutation): an empty list is stated as what it is — no open incidence among the ones
         the server returned — not as "¡Excelente trabajo!" about "tus incidencias". -->
    <v-row v-else-if="filteredIncidents.length === 0">
      <v-col cols="12">
        <v-alert type="info" variant="tonal">
          No hay incidencias abiertas entre las que el servidor devolvió para tu zona.
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col
        v-for="incident in filteredIncidents"
        :key="incident.id"
        cols="12"
      >
        <v-card
          class="incident-card mb-3"
          :class="getIncidentCardClass(incident)"
          elevation="2"
        >
          <v-card-text>
            <v-row align="center">
              <!-- Información principal -->
              <v-col cols="12" md="8">
                <div class="d-flex align-center mb-2">
                  <v-icon
                    :color="getPriorityColor(incident.priority)"
                    class="me-2"
                    size="20"
                  >
                    {{ getPriorityIcon(incident.priority) }}
                  </v-icon>
                  <span class="text-h6 font-weight-medium">{{ incident.title }}</span>
                  <v-chip
                    :color="getStatusColor(incident.status)"
                    size="small"
                    class="ml-3"
                    label
                  >
                    {{ getStatusText(incident.status) }}
                  </v-chip>
                </div>

                <p class="text-body-2 text-grey-darken-1 mb-2">{{ incident.description }}</p>

                <div class="d-flex align-center flex-wrap gap-2">
                  <v-chip
                    :color="getCategoryColor(incident.category)"
                    size="small"
                    variant="outlined"
                  >
                    <v-icon left size="14">mdi-tag</v-icon>
                    {{ incident.category || 'Sin categoría' }}
                  </v-chip>

                  <v-chip
                    :color="getPriorityColor(incident.priority)"
                    size="small"
                    variant="outlined"
                  >
                    <v-icon left size="14">mdi-flag</v-icon>
                    {{ incident.priority }}
                  </v-chip>

                  <v-chip
                    color="blue"
                    size="small"
                    variant="outlined"
                  >
                    <v-icon left size="14">mdi-map-marker</v-icon>
                    {{ incident.point?.address || incident.point?.name || 'Sin ubicación' }}
                  </v-chip>
                </div>
              </v-col>

              <!-- Metadatos -->
              <v-col cols="12" md="4">
                <div class="text-right">
                  <div class="mb-2">
                    <v-icon size="16" class="me-1">mdi-calendar</v-icon>
                    <span class="text-caption">Creado: {{ formatDate(incident.createdAt) }}</span>
                  </div>

                  <div class="mb-2" v-if="incident.assignedTo">
                    <v-icon size="16" class="me-1">mdi-account</v-icon>
                    <span class="text-caption">Asignado a: {{ incident.assignedTo }}</span>
                  </div>

                  <div class="mb-2">
                    <v-icon size="16" class="me-1">mdi-identifier</v-icon>
                    <span class="text-caption">ID: #{{ incident.id }}</span>
                  </div>

                  <div v-if="incident.updatedAt && incident.updatedAt !== incident.createdAt">
                    <v-icon size="16" class="me-1">mdi-update</v-icon>
                    <span class="text-caption">Actualizado: {{ formatDate(incident.updatedAt) }}</span>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Indicador de tiempo transcurrido -->
            <v-row class="mt-3">
              <v-col cols="12">
                <div class="d-flex align-center">
                  <v-icon size="16" class="me-2">mdi-clock-outline</v-icon>
                  <span class="text-caption">
                    Tiempo transcurrido: {{ getTimeElapsed(incident.createdAt) }}
                  </span>
                  <v-spacer></v-spacer>
                  <div class="incident-actions">
                    <v-btn
                      size="small"
                      variant="text"
                      color="primary"
                      @click="viewDetails(incident)"
                    >
                      <v-icon left size="16">mdi-eye</v-icon>
                      Ver Detalles
                    </v-btn>
                    
                    <v-btn
                      size="small"
                      variant="text"
                      color="orange"
                      @click="addComment(incident)"
                    >
                      <v-icon left size="16">mdi-comment-plus</v-icon>
                      Comentar
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- P13-V7B (refutation): the per-card time progress bar is RETIRED — it filled against the
                 same invented 1/3/7-day limits as "Atrasadas". The elapsed time above is a fact. -->
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog de detalles -->
    <v-dialog v-model="showDetailsDialog" max-width="900px">
      <v-card v-if="selectedIncident">
        <v-card-title class="d-flex align-center">
          <v-icon
            :color="getPriorityColor(selectedIncident.priority)"
            class="me-2"
          >
            {{ getPriorityIcon(selectedIncident.priority) }}
          </v-icon>
          {{ selectedIncident.title }}
          <v-spacer></v-spacer>
          <v-chip
            :color="getStatusColor(selectedIncident.status)"
            size="small"
          >
            {{ getStatusText(selectedIncident.status) }}
          </v-chip>
          <v-btn icon @click="showDetailsDialog = false" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <div class="details-section">
                <h4 class="mb-3">Información General</h4>
                <div class="detail-item">
                  <strong>Estado:</strong>
                  <v-chip
                    :color="getStatusColor(selectedIncident.status)"
                    size="small"
                    class="ml-2"
                  >
                    {{ getStatusText(selectedIncident.status) }}
                  </v-chip>
                </div>
                <div class="detail-item">
                  <strong>Prioridad:</strong>
                  <v-chip
                    :color="getPriorityColor(selectedIncident.priority)"
                    size="small"
                    class="ml-2"
                  >
                    {{ selectedIncident.priority }}
                  </v-chip>
                </div>
                <div class="detail-item">
                  <strong>Categoría:</strong> {{ selectedIncident.category }}
                </div>
                <div class="detail-item">
                  <strong>ID:</strong> #{{ selectedIncident.id }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="details-section">
                <h4 class="mb-3">Detalles Operativos</h4>
                <div class="detail-item">
                  <strong>Creado:</strong> {{ formatDate(selectedIncident.createdAt) }}
                </div>
                <div class="detail-item" v-if="selectedIncident.updatedAt !== selectedIncident.createdAt">
                  <strong>Última actualización:</strong> {{ formatDate(selectedIncident.updatedAt) }}
                </div>
                <div class="detail-item">
                  <strong>Tiempo transcurrido:</strong> {{ getTimeElapsed(selectedIncident.createdAt) }}
                </div>
                <div class="detail-item" v-if="selectedIncident.assignedTo">
                  <strong>Asignado a:</strong> {{ selectedIncident.assignedTo }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="details-section">
            <h4 class="mb-3">Descripción</h4>
            <p>{{ selectedIncident.description }}</p>
          </div>

          <div class="details-section mt-4" v-if="selectedIncident.location">
            <h4 class="mb-3">Ubicación</h4>
            <p>{{ selectedIncident.location.address || 'Sin dirección especificada' }}</p>
            <div v-if="selectedIncident.location.lat && selectedIncident.location.lng" class="text-caption text-grey">
              Coordenadas: {{ selectedIncident.location.lat }}, {{ selectedIncident.location.lng }}
            </div>
          </div>

          <div v-if="selectedIncident.comments && selectedIncident.comments.length > 0" class="details-section mt-4">
            <h4 class="mb-3">Historial de Comentarios</h4>
            <div class="comments-container">
              <v-card
                v-for="comment in selectedIncident.comments"
                :key="comment.id"
                variant="outlined"
                class="mb-3"
              >
                <v-card-text class="pa-3">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <div class="d-flex align-center">
                      <v-avatar size="24" color="primary" class="me-2">
                        <span class="text-caption">{{ getInitials(comment.userName) }}</span>
                      </v-avatar>
                      <span class="font-weight-medium">{{ comment.userName }}</span>
                    </div>
                    <span class="text-caption text-grey">{{ formatDate(comment.createdAt) }}</span>
                  </div>
                  <p class="mb-0">{{ comment.comment }}</p>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="orange"
            variant="outlined"
            @click="addCommentFromDetails"
          >
            <v-icon left>mdi-comment-plus</v-icon>
            Añadir Comentario
          </v-btn>
          <v-btn @click="showDetailsDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para añadir comentario -->
    <v-dialog v-model="showCommentDialog" max-width="500px">
      <v-card v-if="selectedIncident">
        <v-card-title>
          <v-icon left>mdi-comment-plus</v-icon>
          Añadir Comentario
        </v-card-title>
        <v-card-subtitle>
          Incidencia: {{ selectedIncident.title }}
        </v-card-subtitle>
        <v-card-text>
          <v-textarea
            v-model="newComment"
            label="Tu comentario"
            rows="4"
            variant="outlined"
            :rules="[v => !!v || 'El comentario es requerido']"
            placeholder="Escribe tu comentario sobre el estado de esta incidencia..."
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showCommentDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="submittingComment"
            @click="submitComment"
          >
            Añadir Comentario
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card-text>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import SupervisorService from '@/services/supervisorService'
import type { Incident } from '@/types'

// ================== PROPS ==================
interface Props {
  supervisorId: number
}

const props = defineProps<Props>()

// ================== EMITS ==================
const emit = defineEmits<{
  'refresh-stats': []
}>()

// ================== ESTADO REACTIVO ==================
const loading = ref(false)
const submittingComment = ref(false)
const incidents = ref<Incident[]>([])
const filteredIncidents = ref<Incident[]>([])

// Filtros
const statusFilter = ref('')
const priorityFilter = ref('')

// Dialogs
const showDetailsDialog = ref(false)
const showCommentDialog = ref(false)
const selectedIncident = ref<Incident | null>(null)
const newComment = ref('')

// Opciones para filtros
// P13-V7B (refutation): the list holds every open state the service admits, so the filter offers the
// third one too (`pending_validation` was listed but could not be selected).
const activeStatusOptions = [
  { title: 'Pendiente', value: 'pending' },
  { title: 'En Progreso', value: 'in_progress' },
  { title: 'Pendiente de validación', value: 'pending_validation' }
]

const priorityOptions = [
  { title: 'Crítica', value: 'critical' },
  { title: 'Alta', value: 'high' },
  { title: 'Media', value: 'medium' },
  { title: 'Baja', value: 'low' }
]

/** P13-V7B (refutation): the canonical vocabulary of the server (Incidence.status / Incidence.priority). */
const statusOf = (incident: Incident) => String((incident as any)?.status || '').toLowerCase()
const priorityOf = (incident: Incident) => String((incident as any)?.priority || '').toLowerCase()

/** P13-V7B (refutation): set only once the server answered — until then no metric is published. */
const loaded = ref(false)
const loadError = ref('')

// ================== COMPUTED ==================
/**
 * P13-V7B (refutation) — counts of the LISTED rows, in the server's own vocabulary, or `null` when no
 * list was received. "En Progreso" used to count `pending` as well; "Atrasadas" is retired (see the
 * template).
 */
const metrics = computed(() => {
  if (!loaded.value) return { inProgress: null as number | null, highPriority: null as number | null }
  return {
    inProgress: incidents.value.filter((i) => statusOf(i) === 'in_progress').length,
    highPriority: incidents.value.filter((i) => ['high', 'critical'].includes(priorityOf(i))).length
  }
})

const displayMetric = (value: number | null) => (typeof value === 'number' ? value : 'Sin datos')

// ================== MÉTODOS ==================
/**
 * Carga las incidencias activas del supervisor
 */
const loadIncidents = async () => {
  if (!props.supervisorId) return

  try {
    loading.value = true
    const response = await SupervisorService.getMyActiveIncidents(props.supervisorId)
    // P13-V7B (refutation): only a LIST is a list — anything else is a failed read, never "0 abiertas".
    if (!Array.isArray(response)) throw new Error('La respuesta de incidencias abiertas no es una lista.')
    incidents.value = response
    loaded.value = true
    loadError.value = ''
    applyFilters()
  } catch (error: any) {
    console.error('Error loading active incidents:', error)
    incidents.value = []
    filteredIncidents.value = []
    loaded.value = false
    loadError.value = error?.message || 'No fue posible cargar las incidencias abiertas de la zona.'
  } finally {
    loading.value = false
  }
}

/**
 * Aplica filtros a las incidencias
 */
const applyFilters = () => {
  let filtered = [...incidents.value]

  if (statusFilter.value) {
    filtered = filtered.filter(incident => incident.status === statusFilter.value)
  }

  if (priorityFilter.value) {
    filtered = filtered.filter(incident => incident.priority === priorityFilter.value)
  }

  filteredIncidents.value = filtered
}

/**
 * Actualiza los datos
 */
const refreshData = () => {
  loadIncidents()
  emit('refresh-stats')
}

/**
 * Ver detalles de la incidencia
 */
const viewDetails = async (incident: Incident) => {
  try {
    const response = await SupervisorService.getIncidentDetails(props.supervisorId, incident.id)
    selectedIncident.value = response
    showDetailsDialog.value = true
  } catch (error) {
    console.error('Error loading incident details:', error)
  }
}

/**
 * Añadir comentario
 */
const addComment = (incident: Incident) => {
  selectedIncident.value = incident
  newComment.value = ''
  showCommentDialog.value = true
}

/**
 * Añadir comentario desde detalles
 */
const addCommentFromDetails = () => {
  showDetailsDialog.value = false
  showCommentDialog.value = true
  newComment.value = ''
}

/**
 * Envía el comentario
 */
const submitComment = async () => {
  if (!selectedIncident.value || !newComment.value.trim()) return
  
  try {
    submittingComment.value = true
    // P13-V6B@V1 — ruta canónica de comments (firma sin supervisorId; incidentId primero).
    await SupervisorService.addIncidentComment(
      selectedIncident.value.id,
      newComment.value.trim()
    )
    showCommentDialog.value = false
    loadIncidents()
    emit('refresh-stats')
    
    // Actualizar los detalles si están abiertos
    if (showDetailsDialog.value) {
      viewDetails(selectedIncident.value)
    }
  } catch (error) {
    console.error('Error adding comment:', error)
  } finally {
    submittingComment.value = false
  }
}

// P13-V7B (refutation): `isOverdue`, `getProgressColor` and `getProgressValue` were REMOVED. They
// measured every incidence against 1/3/7-day limits written here, keyed on 'alta'/'media' while the
// server sends 'high'/'medium' — a lateness verdict with no source.

/**
 * Obtener días transcurridos desde una fecha
 */
const getDaysSince = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - date.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * Obtener tiempo transcurrido en formato legible
 */
const getTimeElapsed = (dateString: string) => {
  const days = getDaysSince(dateString)
  if (days === 0) return 'Hoy'
  if (days === 1) return '1 día'
  if (days < 30) return `${days} días`
  const months = Math.floor(days / 30)
  return months === 1 ? '1 mes' : `${months} meses`
}

/**
 * Obtiene el color según la prioridad
 * P13-V7B (refutation): keyed on the server's vocabulary; the Spanish keys never matched a row.
 */
const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'red-darken-2'
    case 'high': return 'red'
    case 'medium': return 'orange'
    case 'low': return 'green'
    default: return 'grey'
  }
}

/**
 * Obtiene el ícono según la prioridad
 */
const getPriorityIcon = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'mdi-alert-octagon'
    case 'high': return 'mdi-alert'
    case 'medium': return 'mdi-alert-outline'
    case 'low': return 'mdi-information'
    default: return 'mdi-help-circle'
  }
}

/**
 * Obtiene el color según el estado
 * P13-V7B (refutation): canonical states (`resolved` is not one; `pending_validation` is).
 */
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'warning'
    case 'in_progress': return 'info'
    case 'pending_validation': return 'purple'
    case 'closed': return 'success'
    case 'cancelled': return 'grey'
    default: return 'grey'
  }
}

/**
 * Obtiene el texto del estado
 */
const getStatusText = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'Pendiente'
    case 'in_progress': return 'En Progreso'
    case 'pending_validation': return 'Pendiente de validación'
    case 'closed': return 'Cerrada'
    case 'cancelled': return 'Cancelada'
    default: return 'Desconocido'
  }
}

/**
 * Obtiene el color de la categoría
 */
const getCategoryColor = (category: string) => {
  if (!category) return 'grey'
  const colors = ['blue', 'purple', 'teal', 'indigo', 'cyan']
  const hash = category.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

/**
 * Obtiene la clase CSS para la tarjeta de incidencia
 */
const getIncidentCardClass = (incident: Incident) => {
  // P13-V7B (refutation): no 'overdue' class — there is no deadline to be late against.
  return ['incident-active-card', `priority-${priorityOf(incident)}`].join(' ')
}

/**
 * Obtiene las iniciales de un nombre
 */
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

/**
 * Formatea la fecha
 */
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// ================== CICLO DE VIDA ==================
onMounted(() => {
  loadIncidents()
})

// ================== EXPOSICIÓN ==================
defineExpose({
  refreshData
})
</script>

<style scoped>
.incident-card {
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.incident-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* P13-V7B (refutation): the server's priority vocabulary; the `.overdue` style left with its verdict. */
.incident-active-card.priority-critical {
  border-left-color: #b71c1c;
}

.incident-active-card.priority-high {
  border-left-color: #f44336;
}

.incident-active-card.priority-medium {
  border-left-color: #ff9800;
}

.incident-active-card.priority-low {
  border-left-color: #4caf50;
}

.metric-card {
  transition: transform 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.details-section {
  margin-bottom: 16px;
}

.detail-item {
  margin-bottom: 8px;
  line-height: 1.5;
}

.comments-container {
  max-height: 300px;
  overflow-y: auto;
}

.incident-actions {
  display: flex;
  gap: 8px;
}

.gap-2 {
  gap: 8px;
}
</style>
